import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import InputText from '@/components/UI/InputText/InputText';
import { deepIsEqual, isValidEmail } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAppLanguage from '@/hooks/useAppLanguage';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import { setParamsConfig } from '@/redux/ParamsConfig/paramsConfig.action';
import { getParamsConfig } from '@/redux/ParamsConfig/paramsConfig.reducer';
import administrationServices from '@/services/administrationServices';
import titleService from '@/services/titleService';
import { LANGS } from '@/types';
import { ParamsConfDto } from '@/types/ParametersConfiguration';
import styles from './ParamsConfiguration.module.scss';

type ParamsConfigurationProps = {
  paramsConfiguration: ParamsConfDto;
  setParamsConfiguration: (data: ParamsConfDto) => void;
};

const ParamsConfiguration: React.FC<ParamsConfigurationProps> = (props) => {
  const { paramsConfiguration, setParamsConfiguration } = props;
  const [backData, setBackData] = useState<ParamsConfDto | null>(null);
  const { setLoading: setGlobalLoading } = useGlobalSpinnerContext();
  const { addToast } = useToastContext();
  const getParamsConfiguration = useApiRequest(administrationServices.getParamsConfiguration);
  const saveParamConfiguration = useApiRequest(administrationServices.saveParameter);
  const { t } = useTranslation();
  const lang = useAppLanguage();

  const handleFieldChange = (fieldKey: string, fieldType: string, value: any, fieldLang: LANGS = 'EN') => {
    if (!paramsConfiguration || !backData) return;

    let newData: ParamsConfDto = {
      parameters: paramsConfiguration.parameters.map((field) => field.id === fieldKey
        ? { ...field,
          value: fieldType === 'translatable'
            ? { ...field.value as object, [fieldLang]: value }
            : value, // For other types, just set the value directly
          modified: true,
          isInvalid: fieldType === 'list[email]' && (Array.isArray(value) ? value.some((email) => !isValidEmail(email)) : false),
        }
        : field),
    };
    newData = {
      ...newData,
      modified: !deepIsEqual(newData.parameters, backData.parameters),
    };

    setParamsConfiguration(newData);
  };

  useEffect(() => {
    if (!paramsConfiguration?.modified) {
      setBackData(paramsConfiguration);
    }
  }, [paramsConfiguration?.modified]); // eslint-disable-line react-hooks/exhaustive-deps

  const getParamsConfig = (param?: string) => {
    setGlobalLoading(true);
    getParamsConfiguration()
      .then((data) => {
        let fetchedData = {
          ...data,
          parameters: param ? data.parameters.map((p) => (p.id === param ? p : paramsConfiguration.parameters.find((pc) => pc.id === p.id) || p)) : data.parameters,
        };
        fetchedData = {
          ...fetchedData,
          parameters: fetchedData.parameters.sort((a, b) => a.id.localeCompare(b.id)),
          modified: !deepIsEqual(fetchedData.parameters, paramsConfiguration?.parameters || []),
        };
        setBackData(fetchedData);
        setParamsConfiguration(fetchedData);
      })
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => setGlobalLoading(false));
  };

  const saveParameter = (param: string) => {
    setGlobalLoading(true);
    const paramData = paramsConfiguration.parameters.find((p) => p.id === param);
    saveParamConfiguration(param, paramData)
      .then(() => {
        addToast(t('administration.paramsConfig.paramSavedSuccessfully', { param: paramData?.name?.[lang] ?? param }), 'Success');
        getParamsConfig(param);
      })
      .catch((error) => {
        addToast(error.message, 'ERROR');
        setGlobalLoading(false);
      });
  };

  useEffect(() => {
    getParamsConfig();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    titleService.updatePageTitle(t('dashboard.administration'), t('menu.paramsConfiguration'));
  }, [t, lang]);

  return (
    <div>
      {(paramsConfiguration?.parameters && paramsConfiguration?.parameters?.[0]?.name) && (
        <div>
          {paramsConfiguration.parameters.map((param) => (
            <AccordionPanel
              title={`${param?.name?.[lang] || param.id}`}
              key={`accordion-${param.id}`}
              additionalContentRight={(
                <IcoButtonSimple
                  id={`btn-save-param-${param.id}`}
                  icon="icon-diskette-speichern"
                  handleClick={() => saveParameter(param.id)}
                  disabled={!param?.modified || param?.isInvalid}
                  tooltipText={t('campaign.save')}
                />
              )}
            >
              <div className="w-100">
                {param.type === 'int' && (
                  <InputText
                    id={`input-int-field-param-${param?.id}`}
                    noTranslationField
                    label={`${param.name[lang] || param.id}`}
                    type="number"
                    handleChange={(ev) => handleFieldChange(param?.id, param?.type, ev.target.value)}
                    value={param?.value as string}
                    className="w-100"
                  />
                )}
                {param.type === 'translatable' && (
                  <div className={styles.TranslatableFieldContainer}>
                    {Object.keys(param.value).map((fieldLang) => (
                      <div className={styles.TwoColumnsWithPadding} key={`${param.id}-${fieldLang}`}>
                        <InputText
                          id={`input-translatable-field-param-${param?.id}`}
                          value={param?.value?.[fieldLang]}
                          label={`${param.name[lang] || param.id} [${fieldLang}]`}
                          handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(param?.id, param?.type, ev.target.value, fieldLang as LANGS)}
                          className="w-100"
                          rows={3}
                          multiline
                        />
                      </div>
                    ))}
                  </div>
                )}
                {param.type === 'list[email]' && (
                  <div className={styles.TranslatableFieldContainer}>
                    {(param.value as string[]).map((email, index) => (
                      <div className={styles.EmailInputContainer} key={`${param.id}-${index}`}>
                        <InputText
                          id={`input-list-email-field-param-${param?.id}`}
                          value={email}
                          label={`${param.name[lang] || param.id}`}
                          handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(param?.id, param?.type, (param.value as string[]).map((e, i) => (i === index ? ev.target.value : e)))}
                          className="w-100"
                          clear={() => handleFieldChange(param?.id, param?.type, (param.value as string[]).filter((e, i) => i !== index))}
                          invalid={!isValidEmail(email)}
                          helperText={!isValidEmail(email) ? [t('validations.formatEmail')] : []}
                        />
                      </div>
                    ))}
                    <IcoButtonSimple
                      id={`btn-add-param-${param.id}`}
                      icon="icon-plus-hinzufuegen-klein"
                      handleClick={() => handleFieldChange(param?.id, param?.type, [...(param.value as string[]), ''])}
                      tooltipText={t('add')}
                    />
                  </div>
                )}
              </div>
            </AccordionPanel>
          ))}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  paramsConfiguration: getParamsConfig(state),
});

const actionCreators = {
  setParamsConfiguration: setParamsConfig,
};

export default connect(mapStateToProps, actionCreators)(ParamsConfiguration);
